import type { IPin } from '../types/Pin';
import type { IRecording } from '../types/Recording';
import { isBusPin } from '../types/Pin';

export interface EpisodeMigrationResult {
  episodeAlbumKey: string;
  pinsUpdated: number;
  recordingsUpdated: number;
}

/** Pin types that keep their specialised collection behaviour in episode projects. */
const PRESERVED_PIN_TYPES = new Set([
  'bus',
  'wanderPin',
  'collaborativeStem',
  'closeCluster',
]);

/**
 * Sentinel written onto episode-project recordings from the episode list UI.
 * It is not a Pins document id — recordings belong to the project album, not a map pin.
 */
export const EPISODE_LIST_RECORDING_PIN_KEY = 'episode';

export function isEpisodeListRecordingPinKey(
  pinKey: string | null | undefined,
): boolean {
  return pinKey === EPISODE_LIST_RECORDING_PIN_KEY;
}

/** True when episode health checks should run (toggle, album key, or episode pins). */
export function isEpisodeProjectScope(
  project: { isEpisodeProjectEnabled?: boolean; episodeAlbumKey?: string | null } | null | undefined,
  pins: IPin[]
): boolean {
  if (!project) return false;
  if (project.isEpisodeProjectEnabled === true) return true;
  if (project.episodeAlbumKey?.trim()) return true;
  return pins.some((pin) => pin.PinModel === 'episode');
}

/**
 * Prefers an existing project value, then the most common pin albumKey.
 */
export function resolveEpisodeAlbumKeyForMigration(
  existingEpisodeAlbumKey: string | null | undefined,
  pins: IPin[]
): string | null {
  const trimmed = existingEpisodeAlbumKey?.trim();
  if (trimmed) return trimmed;

  const albumKeys = pins
    .map((pin) => pin.albumKey?.trim())
    .filter((key): key is string => Boolean(key));

  if (albumKeys.length === 0) return null;

  const counts = new Map<string, number>();
  for (const key of albumKeys) {
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  let bestKey = albumKeys[0];
  let bestCount = 0;
  for (const [key, count] of counts) {
    if (count > bestCount) {
      bestKey = key;
      bestCount = count;
    }
  }
  return bestKey;
}

/** Target pinType when a project becomes an episode project. */
export function resolveEpisodePinType(pin: IPin, isQREnabled: boolean): string {
  if (isBusPin(pin)) return 'bus';
  if (pin.pinType && PRESERVED_PIN_TYPES.has(pin.pinType)) {
    return pin.pinType;
  }
  if (isQREnabled && (pin.pinType === 'qrCode' || pin.pinType === 'QREpisode' || pin.qrPath)) {
    return 'QREpisode';
  }
  return 'episode';
}

export function buildEpisodePinMigrationUpdate(
  pin: IPin,
  episodeAlbumKey: string,
  isQREnabled: boolean
): Partial<IPin> & { PinModel: string } {
  return {
    albumKey: episodeAlbumKey,
    PinModel: 'episode',
    pinType: resolveEpisodePinType(pin, isQREnabled),
  };
}

export function sortRecordingsForEpisodeMigration(recordings: IRecording[]): IRecording[] {
  return [...recordings].sort((a, b) => {
    const orderA = a.sequenceNumber ?? a.collectionOrder ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.sequenceNumber ?? b.collectionOrder ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;
    const titleA = a.file?.title ?? '';
    const titleB = b.file?.title ?? '';
    return titleA.localeCompare(titleB);
  });
}

export function buildEpisodeRecordingMigrationUpdates(
  recordings: IRecording[],
  episodeAlbumKey: string,
  introRecordingKey?: string | null
): Array<{ key: string; updates: Partial<IRecording> }> {
  const eligible = recordings.filter(
    (recording) => !introRecordingKey || recording.key !== introRecordingKey
  );
  const sorted = sortRecordingsForEpisodeMigration(eligible);

  return sorted.map((recording, index) => ({
    key: recording.key,
    updates: {
      albumKey: episodeAlbumKey,
      sequenceNumber: index + 1,
    },
  }));
}

/** Renumber shared episode album recordings to contiguous 1..n after a delete or reorder. */
export function buildEpisodeAlbumSequenceRenumberUpdates(
  recordings: IRecording[]
): Array<{ key: string; sequenceNumber: number }> {
  const sorted = sortRecordingsForEpisodeMigration(recordings);

  return sorted
    .map((recording, index) => ({
      key: recording.key,
      sequenceNumber: index + 1,
    }))
    .filter((update, index) => sorted[index].sequenceNumber !== update.sequenceNumber);
}

/** Assign 1..n sequence numbers for an explicit episode recording order (e.g. after reorder). */
export function buildEpisodeRecordingOrderUpdates(
  orderedRecordings: Array<Pick<IRecording, 'key'>>
): Array<{ key: string; sequenceNumber: number }> {
  return orderedRecordings.map((recording, index) => ({
    key: recording.key,
    sequenceNumber: index + 1,
  }));
}

export function pinNeedsEpisodeMigration(
  pin: IPin,
  episodeAlbumKey: string
): boolean {
  const pinAlbumKey = pin.albumKey?.trim();
  return pin.PinModel !== 'episode' || !pinAlbumKey || pinAlbumKey !== episodeAlbumKey;
}

export interface EpisodeProjectHealthIssue {
  code: string;
  severity: 'error' | 'warning';
  message: string;
}

export interface EpisodeProjectHealthResult {
  isHealthy: boolean;
  issues: EpisodeProjectHealthIssue[];
  episodeAlbumKey: string | null;
  canAutoFix: boolean;
}

const AUTO_FIXABLE_ISSUE_CODES = new Set([
  'missing_episode_album_key',
  'missing_episode_album_document',
  'episode_album_key_not_on_project',
  'missing_pin_model',
  'pins_missing_album_key',
  'pin_album_mismatch',
  'multiple_pin_albums',
  'recordings_missing_album_key',
  'recordings_not_on_shared_album',
  'orphan_episode_recordings',
]);

/**
 * Checks whether an episode project's pins, albums, and recordings match
 * what the mobile app expects. Used to warn admins before users hit collection bugs.
 */
export function validateEpisodeProjectHealth(input: {
  episodeAlbumKey?: string | null;
  pins: IPin[];
  recordings: IRecording[];
  introRecordingKey?: string | null;
  /** When false, the shared episode album key has no matching Album document in Firestore. */
  episodeAlbumExists?: boolean;
}): EpisodeProjectHealthResult {
  const issues: EpisodeProjectHealthIssue[] = [];
  const projectAlbumKey = input.episodeAlbumKey?.trim() || null;
  const inferredAlbumKey = resolveEpisodeAlbumKeyForMigration(projectAlbumKey, input.pins);
  const albumKeyForChecks = projectAlbumKey ?? inferredAlbumKey;

  if (input.pins.length === 0) {
    issues.push({
      code: 'no_pins',
      severity: 'warning',
      message: 'This episode project has no pin locations yet.',
    });
  }

  if (!projectAlbumKey && inferredAlbumKey) {
    issues.push({
      code: 'episode_album_key_not_on_project',
      severity: 'error',
      message: `Pins use album "${inferredAlbumKey}" but the project is missing episodeAlbumKey.`,
    });
  }

  if (!albumKeyForChecks && input.pins.length > 0) {
    issues.push({
      code: 'missing_episode_album_key',
      severity: 'error',
      message: 'This episode project has no shared episode album key.',
    });
  }

  if (albumKeyForChecks) {
    if (input.episodeAlbumExists === false) {
      issues.push({
        code: 'missing_episode_album_document',
        severity: 'error',
        message: `Episode album "${albumKeyForChecks}" is referenced on this project but no Album document exists. Create the album or run Repair project.`,
      });
    }

    const pinsMissingModel = input.pins.filter((pin) => pin.PinModel !== 'episode');
    if (pinsMissingModel.length > 0) {
      const examples = pinsMissingModel
        .slice(0, 3)
        .map((pin) => pin.name || pin.pinKey)
        .join(', ');
      issues.push({
        code: 'missing_pin_model',
        severity: 'error',
        message: `${pinsMissingModel.length} pin(s) are missing PinModel: "episode" (required by the mobile app).${examples ? ` e.g. ${examples}` : ''}`,
      });
    }

    const pinsMissingAlbumKey = input.pins.filter((pin) => !pin.albumKey?.trim());
    if (pinsMissingAlbumKey.length > 0) {
      const examples = pinsMissingAlbumKey
        .slice(0, 3)
        .map((pin) => pin.name || pin.pinKey)
        .join(', ');
      issues.push({
        code: 'pins_missing_album_key',
        severity: 'error',
        message: `${pinsMissingAlbumKey.length} pin(s) are missing an album key.${examples ? ` e.g. ${examples}` : ''}`,
      });
    }

    const distinctAlbumKeys = new Set(
      input.pins.map((pin) => pin.albumKey).filter((key): key is string => Boolean(key))
    );
    if (distinctAlbumKeys.size > 1) {
      issues.push({
        code: 'multiple_pin_albums',
        severity: 'error',
        message: `Pins use ${distinctAlbumKeys.size} different album keys; episode projects need one shared album (${albumKeyForChecks}).`,
      });
    }

    const pinsWrongAlbum = input.pins.filter(
      (pin) => pin.albumKey && pin.albumKey !== albumKeyForChecks
    );
    if (pinsWrongAlbum.length > 0 && distinctAlbumKeys.size <= 1) {
      issues.push({
        code: 'pin_album_mismatch',
        severity: 'error',
        message: `${pinsWrongAlbum.length} pin(s) do not use the shared episode album (${albumKeyForChecks}).`,
      });
    }

    const nonIntroRecordings = input.recordings.filter(
      (recording) => !input.introRecordingKey || recording.key !== input.introRecordingKey
    );
    const sharedAlbumRecordings = nonIntroRecordings.filter(
      (recording) => recording.albumKey === albumKeyForChecks
    );
    const offAlbumRecordings = nonIntroRecordings.filter(
      (recording) => recording.albumKey && recording.albumKey !== albumKeyForChecks
    );
    const recordingsMissingAlbumKey = nonIntroRecordings.filter(
      (recording) => !recording.albumKey?.trim()
    );

    if (recordingsMissingAlbumKey.length > 0) {
      issues.push({
        code: 'recordings_missing_album_key',
        severity: 'error',
        message: `${recordingsMissingAlbumKey.length} recording(s) are missing an album key.`,
      });
    }

    if (nonIntroRecordings.length > 0 && sharedAlbumRecordings.length === 0) {
      if (recordingsMissingAlbumKey.length === 0) {
        issues.push({
          code: 'recordings_not_on_shared_album',
          severity: 'error',
          message: 'Recordings exist on this project but none are assigned to the shared episode album.',
        });
      }
    } else if (sharedAlbumRecordings.length === 0) {
      issues.push({
        code: 'no_recordings',
        severity: 'warning',
        message: 'No recordings in the shared episode album yet. Users will not be able to play content after collecting.',
      });
    }

    if (offAlbumRecordings.length > 0 && sharedAlbumRecordings.length > 0) {
      issues.push({
        code: 'orphan_episode_recordings',
        severity: 'warning',
        message: `${offAlbumRecordings.length} recording(s) are still on old per-pin albums and should be moved to the shared album.`,
      });
    }
  }

  const canAutoFix = issues.some((issue) => AUTO_FIXABLE_ISSUE_CODES.has(issue.code));

  return {
    isHealthy: issues.length === 0,
    issues,
    episodeAlbumKey: albumKeyForChecks,
    canAutoFix,
  };
}
